
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, Minus, RotateCcw } from "lucide-react";

interface User {
  id: string;
  username: string | null;
  full_name: string | null;
  email: string;
  credits_balance: number;
  credits_used: number;
  available_credits: number;
}

interface CreditAdjustmentDialogProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreditAdjustmentDialog({
  user,
  isOpen,
  onClose,
  onSuccess
}: CreditAdjustmentDialogProps) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [operation, setOperation] = useState<"add" | "subtract" | "reset">("add");

  const handleClose = () => {
    setAmount("");
    setDescription("");
    setOperation("add");
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (operation !== "reset" && (!amount || parseFloat(amount) <= 0)) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    setIsLoading(true);

    try {
      if (operation === "reset") {
        const { error } = await supabase.rpc('reset_user_credit_usage', {
          target_user_id: user.id
        });

        if (error) throw error;
        toast.success("Credit usage reset successfully");
      } else {
        const creditAmount = operation === "add" ? parseFloat(amount) : -parseFloat(amount);
        
        // Validate subtract operation doesn't result in negative balance
        if (operation === "subtract" && parseFloat(amount) > user.credits_balance) {
          toast.error("Cannot subtract more credits than available balance");
          setIsLoading(false);
          return;
        }

        const { error } = await supabase.rpc('manage_user_credits', {
          target_user_id: user.id,
          amount: creditAmount,
          description: description.trim()
        });

        if (error) throw error;
        
        const actionText = operation === "add" ? "added to" : "subtracted from";
        toast.success(`${amount} credits ${actionText} ${user.full_name || user.username || user.email}`);
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Credit adjustment error:', error);
      toast.error(`Failed to ${operation} credits: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviewBalance = () => {
    if (!user || operation === "reset") return user?.credits_balance || 0;
    const creditAmount = parseFloat(amount) || 0;
    if (operation === "add") {
      return user.credits_balance + creditAmount;
    } else {
      return Math.max(0, user.credits_balance - creditAmount);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {operation === "add" && <Plus className="h-5 w-5 text-green-600" />}
            {operation === "subtract" && <Minus className="h-5 w-5 text-red-600" />}
            {operation === "reset" && <RotateCcw className="h-5 w-5 text-blue-600" />}
            {operation === "add" ? "Add Credits" : operation === "subtract" ? "Subtract Credits" : "Reset Credit Usage"}
          </DialogTitle>
          <DialogDescription>
            Manage credits for this user. All changes will be logged for audit purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Info */}
          <div className="bg-muted p-3 rounded-lg">
            <div className="font-medium">{user.full_name || user.username || 'Unnamed User'}</div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm">Current Balance:</span>
              <Badge variant="secondary">{user.credits_balance} credits</Badge>
              <span className="text-sm">Available:</span>
              <Badge variant={user.available_credits === 0 ? "destructive" : "default"}>
                {user.available_credits} credits
              </Badge>
            </div>
          </div>

          {/* Operation Selection */}
          <div className="space-y-2">
            <Label>Operation</Label>
            <div className="flex gap-2">
              <Button
                variant={operation === "add" ? "default" : "outline"}
                size="sm"
                onClick={() => setOperation("add")}
                className="flex-1"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
              <Button
                variant={operation === "subtract" ? "default" : "outline"}
                size="sm"
                onClick={() => setOperation("subtract")}
                className="flex-1"
              >
                <Minus className="h-4 w-4 mr-1" />
                Subtract
              </Button>
              <Button
                variant={operation === "reset" ? "default" : "outline"}
                size="sm"
                onClick={() => setOperation("reset")}
                className="flex-1"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset Usage
              </Button>
            </div>
          </div>

          {/* Amount Input (hidden for reset) */}
          {operation !== "reset" && (
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                max={operation === "subtract" ? user.credits_balance : undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter credit amount"
              />
              {operation === "subtract" && parseFloat(amount) > user.credits_balance && (
                <p className="text-sm text-red-600">
                  Amount cannot exceed current balance ({user.credits_balance})
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                operation === "reset" 
                  ? "Reason for resetting credit usage..." 
                  : `Reason for ${operation === "add" ? "adding" : "subtracting"} credits...`
              }
              rows={3}
            />
          </div>

          {/* Preview */}
          {operation !== "reset" && amount && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm font-medium">Preview:</div>
              <div className="text-sm">
                New Balance: <Badge variant="secondary">{getPreviewBalance()} credits</Badge>
              </div>
            </div>
          )}

          {operation === "reset" && (
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <div className="text-sm font-medium text-amber-800">Reset Credit Usage</div>
              <div className="text-sm text-amber-700">
                This will reset the user's credit usage to 0 for the current period, 
                allowing them to use their full credit balance again.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {operation === "add" ? "Add Credits" : operation === "subtract" ? "Subtract Credits" : "Reset Usage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
